/**
 * Template API client.
 *
 * `listVisible` on the server already filters private templates the viewer does not own — but that
 * is a listing filter, never the authorization decision. Every individual read is still decided
 * server-side, and a private template returns 404 rather than 403
 * (docs/engineering/04-authorization.md).
 */

import {
  isApiErrorBody,
  type ErrorCode,
  type TemplateDefinition,
  type TemplateVisibility,
} from '@pius/contracts';

import { request } from '../../api/client';

const BASE = '/api/v1/templates';

export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode,
    message: string,
    readonly correlationId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface TemplateSummary {
  id: string;
  name: string;
  owner_display: string | null;
  visibility: TemplateVisibility;
  retired_at: string | null;
  latest_published_version: number | null;
  /**
   * The id creating a Liturgy needs. Null when nothing is published yet — which is exactly what the
   * creation wizard filters on, since a draft cannot be instantiated from.
   */
  latest_published_version_id: string | null;
  has_draft: boolean;
  designations: string[];
  /** Outermost first: this template, then what it specializes, up to the root. */
  lineage: string[];
  /**
   * Whether the viewer owns this template — a fact about the Resource, not a permission.
   *
   * Reported separately from `can_edit` on purpose. The two agree today, and a client that filters
   * "which of these are mine" on `can_edit` is reading an authorization outcome as a statement of
   * ownership; the moment a non-owner may contribute, that client starts calling other people's
   * templates the reader's own.
   */
  is_owner: boolean;
  can_edit: boolean;
}

export async function fetchTemplates(): Promise<TemplateSummary[]> {
  const res = await fetch(BASE, { credentials: 'include' });
  const payload: unknown = await res.json().catch(() => undefined);

  if (!res.ok) {
    if (isApiErrorBody(payload)) {
      throw new ApiError(payload.error.code, payload.error.message, payload.error.correlation_id);
    }
    throw new ApiError('internal_error', 'Templates could not be loaded.');
  }

  return (payload as { data: TemplateSummary[] }).data;
}

// ── authoring ───────────────────────────────────────────────────────────────

export interface TemplateVersionView {
  id: string;
  version_number: number;
  published_at: string | null;
  is_published: boolean;
  definition: TemplateDefinition;
}

export interface TemplateDetail {
  id: string;
  name: string;
  visibility: TemplateVisibility;
  parent_template_id: string | null;
  retired_at: string | null;
  may_edit: boolean;
  versions: TemplateVersionView[];
}

export function fetchTemplate(id: string): Promise<TemplateDetail> {
  return request(`/templates/${id}`);
}

export function createTemplate(input: {
  name: string;
  visibility: TemplateVisibility;
}): Promise<{ id: string; name: string }> {
  return request('/templates', { method: 'POST', body: JSON.stringify(input) });
}

export function duplicateTemplate(
  id: string,
  input?: { name?: string; visibility?: TemplateVisibility },
): Promise<{ id: string; name: string }> {
  return request(`/templates/${id}/duplicate`, {
    method: 'POST',
    body: JSON.stringify(input ?? {}),
  });
}

/** "Edit this template" means "start the next version" — a published definition is frozen. */
export function startDraft(
  id: string,
  definition?: TemplateDefinition,
): Promise<TemplateVersionView> {
  return request(`/templates/${id}/draft`, {
    method: 'POST',
    body: JSON.stringify(definition ? { definition } : {}),
  });
}

export function saveDraft(id: string, definition: TemplateDefinition): Promise<{ ok: boolean }> {
  return request(`/templates/${id}/draft`, {
    method: 'PUT',
    body: JSON.stringify({ definition }),
  });
}

export interface PublishPreview {
  /** Always false. Stated by the server so the modal is not asserting it on its own authority. */
  existing_liturgies_change: boolean;
  pinned: { version_id: string; version_number: number; liturgy_count: number }[];
}

export function fetchPublishPreview(id: string): Promise<PublishPreview> {
  return request(`/templates/${id}/publish-preview`);
}

export function publishDraft(
  id: string,
): Promise<{ version_id: string; version_number: number; published_at: string }> {
  return request(`/templates/${id}/publish`, { method: 'POST' });
}

// ── localization ────────────────────────────────────────────────────────────

export function fetchLocalizations(
  id: string,
  versionId: string,
): Promise<{ data: { language_code: string; messages: Record<string, string> }[] }> {
  return request(`/templates/${id}/versions/${versionId}/localizations`);
}

export function saveLocalization(
  id: string,
  versionId: string,
  language: string,
  messages: Record<string, string>,
): Promise<{ ok: boolean }> {
  return request(`/templates/${id}/versions/${versionId}/localizations/${language}`, {
    method: 'PUT',
    body: JSON.stringify({ messages }),
  });
}
